import React, { useState, useMemo, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {User, Briefcase, Users, ShieldCheck, MapPin, Crown} from 'lucide-react'

// Superadmin whitelist
const SUPERADMINS = [
  "parfaitkiss@gmail.com",
  "founder@nursinghives.com"
]

// All possible roles
const allRoles = [
  {
    id: "nurse",
    label: "Nurse",
    icon: User,
    description: "I'm a nurse looking for international opportunities"
    // Always visible - public signup
  },
  {
    id: "recruiter",
    label: "Recruiter",
    icon: Briefcase,
    description: "I recruit nurses for healthcare facilities",
    gated: true,
    visibleIf: (email: string, inviteRole?: string) => {
      // Only visible with valid invite token
      return inviteRole === 'recruiter'
    }
  },
  {
    id: "affiliate",
    label: "Affiliate",
    icon: Users,
    description: "I want to refer nurses and earn commissions",
    gated: true,
    visibleIf: (email: string, inviteRole?: string) => {
      // Only visible with valid invite token
      return inviteRole === 'affiliate'
    }
  },
  {
    id: "admin",
    label: "Admin (HQ)",
    icon: ShieldCheck,
    description: "Manage platform operations and verify users",
    gated: true,
    visibleIf: (email: string, inviteRole?: string) => {
      // Only visible with valid invite token
      return inviteRole === 'admin'
    }
  },

  {
    id: "superadmin",
    label: "Superadmin",
    icon: Crown,
    description: "Full platform access and impersonation",
    gated: true,
    visibleIf: (email: string) => SUPERADMINS.includes(email.toLowerCase())
  }
]

export default function RoleSelect() {
  const [selectedRole, setSelectedRole] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const [autoAssigning, setAutoAssigning] = useState(true)
  const navigate = useNavigate()

  // Get invite role and token from URL params (e.g., ?invite=recruiter&token=abc123)
  const searchParams = new URLSearchParams(window.location.search)
  const inviteRole = searchParams.get('invite') || undefined
  const inviteToken = searchParams.get('token') || undefined

  // Auto-assign role on mount if applicable
  useEffect(() => {
    const autoAssignRole = async () => {
      const user = lumi.auth.user
      if (!user) {
        setAutoAssigning(false)
        return
      }

      try {
        // Check if user already has a profile
        const existingProfile = await lumi.entities.UserProfiles.list({
          filter: { userId: user.userId }
        })
        
        if (existingProfile.list.length > 0) {
          // User already has role, redirect to dashboard
          const profile = existingProfile.list[0]
          const roleRoutes: Record<string, string> = {
            nurse: "/dashboard/nurse",
            recruiter: "/dashboard/recruiter",
            affiliate: "/dashboard/affiliate",
            admin: "/dashboard/admin",
            superadmin: "/dashboard/superadmin"
          }
          navigate(roleRoutes[profile.role] || "/dashboard/nurse")
          return
        }

        // Auto-assign for superadmin
        if (SUPERADMINS.includes(user.email.toLowerCase())) {
          await lumi.entities.UserProfiles.create({
            userId: user.userId,
            email: user.email,
            role: "superadmin",
            onboardingComplete: true,
            createdAt: new Date().toISOString()
          })
          
          // Create audit log
          await lumi.entities.AuditLogs.create({
            actor_id: user.userId,
            actor_email: user.email,
            action: "assigned_role",
            target_id: user.userId,
            target_email: user.email,
            metadata: { role: "superadmin", method: "auto_whitelist" },
            created_at: new Date().toISOString()
          })
          
          toast.success("Superadmin access granted")
          navigate("/dashboard/superadmin")
          return
        }

        // Auto-assign for invite-based roles with token validation
        if (inviteRole && inviteToken && ['recruiter', 'affiliate', 'admin'].includes(inviteRole)) {
          // Validate invite token
          const invites = await lumi.entities.Invites.list({
            filter: { 
              token: inviteToken,
              role: inviteRole,
              status: "pending"
            }
          })
          
          if (invites.list.length === 0) {
            toast.error("Invalid or expired invite link")
            setAutoAssigning(false)
            return
          }
          
          const invite = invites.list[0]
          
          // Check expiration
          if (new Date(invite.expires_at) < new Date()) {
            await lumi.entities.Invites.update(invite._id, { status: "expired" })
            toast.error("This invite link has expired")
            setAutoAssigning(false)
            return
          }
          
          // Check if email matches (optional validation)
          if (invite.email && invite.email.toLowerCase() !== user.email.toLowerCase()) {
            toast.error("This invite was sent to a different email address")
            setAutoAssigning(false)
            return
          }
          
          // Create user profile
          await lumi.entities.UserProfiles.create({
            userId: user.userId,
            email: user.email,
            role: inviteRole,
            region: invite.region || undefined,
            onboardingComplete: true,
            createdAt: new Date().toISOString()
          })
          
          // Mark invite as used
          await lumi.entities.Invites.update(invite._id, {
            status: "used",
            used_at: new Date().toISOString(),
            used_by: user.userId,
            usage_count: (invite.usage_count || 0) + 1
          })
          
          // Create audit log
          await lumi.entities.AuditLogs.create({
            actor_id: user.userId,
            actor_email: user.email,
            action: "used_invite",
            target_id: user.userId,
            target_email: user.email,
            metadata: { 
              role: inviteRole, 
              invite_id: invite._id,
              region: invite.region 
            },
            created_at: new Date().toISOString()
          })
          
          const roleLabels: Record<string, string> = {
            recruiter: "Recruiter",
            affiliate: "Affiliate",
            admin: "Admin"
          }
          
          toast.success(`${roleLabels[inviteRole]} role assigned successfully!`)
          
          const roleRoutes: Record<string, string> = {
            recruiter: "/dashboard/recruiter",
            affiliate: "/dashboard/affiliate",
            admin: "/dashboard/admin"
          }
          navigate(roleRoutes[inviteRole])
          return
        }

        // No auto-assignment, show UI
        setAutoAssigning(false)
      } catch (error) {
        console.error("Auto-assignment error:", error)
        toast.error("Failed to validate invite. Please contact support.")
        setAutoAssigning(false)
      }
    }

    autoAssignRole()
  }, [navigate, inviteRole, inviteToken])

  // Filter roles based on user email and invite token
  const roles = useMemo(() => {
    const user = lumi.auth.user
    if (!user?.email) return allRoles.filter(r => !r.gated)
    
    return allRoles.filter(role => {
      if (!role.gated) return true
      return role.visibleIf?.(user.email, inviteRole) ?? false
    })
  }, [lumi.auth.user?.email, inviteRole])

  const handleContinue = async () => {
    if (!selectedRole) {
      toast.error("Please select a role")
      return
    }

    setIsLoading(true)
    try {
      const user = lumi.auth.user
      if (!user) {
        toast.error("Please sign in first")
        navigate("/auth")
        return
      }

      await lumi.entities.UserProfiles.create({
        userId: user.userId,
        email: user.email,
        role: selectedRole,
        onboardingComplete: true,
        createdAt: new Date().toISOString()
      })

      toast.success("Role selected successfully!")
      
      // Direct routing to dashboard (skip profile stepper)
      const roleRoutes: Record<string, string> = {
        nurse: "/dashboard/nurse",
        recruiter: "/dashboard/recruiter",
        affiliate: "/dashboard/affiliate",
        admin: "/dashboard/admin",
        superadmin: "/dashboard/superadmin"
      }
      navigate(roleRoutes[selectedRole] || "/dashboard/nurse")
    } catch (error) {
      toast.error("Failed to save role. Please try again.")
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  // Show loading state during auto-assignment
  if (autoAssigning) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex items-center justify-center px-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Setting up your account...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex items-center justify-center px-6">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Role
          </h1>
          <p className="text-lg text-gray-600">
            Select the option that best describes you
          </p>
          {inviteRole && (
            <div className="mt-4 inline-flex items-center px-4 py-2 rounded-lg bg-teal-50 border border-teal-200">
              <span className="text-sm font-medium text-teal-800">
                🎟️ Special invite: {inviteRole.charAt(0).toUpperCase() + inviteRole.slice(1)} access granted
              </span>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {roles.map((role) => {
            const Icon = role.icon
            const isGated = role.gated ?? false
            return (
              <button
                key={role.id}
                onClick={() => setSelectedRole(role.id)}
                className={`p-6 rounded-xl border-2 transition-all ${
                  selectedRole === role.id
                    ? "border-teal-600 bg-teal-50 shadow-lg"
                    : "border-gray-200 bg-white hover:border-teal-300"
                } ${isGated ? "relative" : ""}`}
              >
                {isGated && (
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      Restricted
                    </span>
                  </div>
                )}
                <Icon className={`w-12 h-12 mx-auto mb-4 ${
                  selectedRole === role.id ? "text-teal-600" : "text-gray-400"
                }`} />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {role.label}
                </h3>
                <p className="text-sm text-gray-600">
                  {role.description}
                </p>
              </button>
            )
          })}
        </div>

        <button
          onClick={handleContinue}
          disabled={!selectedRole || isLoading}
          className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? "Saving..." : "Continue"}
        </button>
      </div>
    </div>
  )
}
